import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";

const LINKS = [
  { label: "Gallery", href: "#gallery" },
  { label: "Amenities", href: "#amenities" },
  { label: "Reviews", href: "#reviews" },
  { label: "The Area", href: "#guide" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = ({ name }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/85 backdrop-blur-xl border-b border-border shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center shrink-0"
            aria-label={name || "MyAZBnB"}
          >
            <img src="/myazbnb-logo.png" alt={name || "MyAZBnB.com"} className="h-12 sm:h-14 md:h-16 w-auto object-contain" />
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(l.href)}
                className={`text-sm font-medium transition-colors hover:text-primary ${scrolled ? "text-foreground" : "text-white/90"}`}
              >
                {t(l.label)}
              </button>
            ))}
            <button
              data-testid="nav-lang-toggle"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary ${scrolled ? "text-foreground" : "text-white/90"}`}
              aria-label="Toggle language"
            >
              <Globe className="h-4 w-4" />
              {lang === "en" ? "ES" : "EN"}
            </button>
            <Button
              data-testid="nav-book-btn"
              onClick={() => go("#book")}
              className="rounded-full bg-primary hover:bg-primary/90 hover:-translate-y-0.5 transition-all shadow-md"
            >
              {t("Book Now")}
            </Button>
          </nav>

          <div className="md:hidden flex items-center gap-3">
            <button
              data-testid="nav-lang-toggle-mobile"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              className={`flex items-center gap-1 text-sm font-medium ${scrolled ? "text-foreground" : "text-white"}`}
              aria-label="Toggle language"
            >
              <Globe className="h-4 w-4" />
              {lang === "en" ? "ES" : "EN"}
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className={`${scrolled ? "text-foreground" : "text-white"}`}
              onClick={() => setOpen(!open)}
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background border-t border-border px-4 py-4 space-y-2">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="block w-full text-left py-2 text-foreground font-medium"
            >
              {t(l.label)}
            </button>
          ))}
          <Button data-testid="nav-book-btn-mobile" onClick={() => go("#book")} className="w-full rounded-full bg-primary">
            {t("Book Now")}
          </Button>
        </div>
      )}
    </header>
  );
};
